import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchClients, fetchClientsForMultipleUsers, clearClientCache } from "./clientSlice";
import { useNavigate, useLocation } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import MobileClientList from "./MobileClientList";
import UnifiedPolicyForm from "../addPolicy/UnifiedPolicyForm";
import ImportPoliciesDialog from "../../components/ImportPoliciesDialog";
import { firebaseCache } from "../../utils/firebaseCache";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PersonIcon from '@mui/icons-material/Person';
import CategoryIcon from '@mui/icons-material/Category';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DescriptionIcon from '@mui/icons-material/Description';
import BusinessIcon from '@mui/icons-material/Business';
import FlagIcon from '@mui/icons-material/Flag';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Pagination from "@mui/material/Pagination";
import Link from "@mui/material/Link";
import useMediaQuery from "@mui/material/useMediaQuery";
import { shouldUseMobileLayout } from "../../utils/deviceDetection";
import { useActivityBasedRefresh } from "../../hooks/useRefresh";
import { useAggregations } from "../../utils/aggregations";
import { toast } from "react-toastify";
import { getClientCountsByStatus } from "../../utils/countUtils";
import { useAdminStaffData } from "../../hooks/useAdminStaffData";
import { useAccountStatus } from "../../hooks/useAccountStatus";

const ClientList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { clients, loading } = useSelector((state) => state.clients);
  const { user } = useSelector((state) => state.auth);
  const { userIds, isAdmin, loading: staffLoading } = useAdminStaffData();
  const { isAccountActive } = useAccountStatus();

  // Initialize aggregations for client statistics
  const { updateClientAggregation } = useAggregations(user?.uid);

  // All hooks must be called before any conditional returns
  const [selectedTab, setSelectedTab] = useState("customers");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const isTinyScreen = useMediaQuery('(max-height:720px)');  // e.g. 13" laptop
  const isShortScreen = useMediaQuery('(max-height:900px)'); // e.g. 14–15" laptop
  const isMediumScreen = useMediaQuery('(max-height:1080px)'); // full HD
  const itemsPerPage = isTinyScreen ? 4 : isShortScreen ? 5 : isMediumScreen ? 6 : 8;


  const isMobile = shouldUseMobileLayout();
  const isSmallScreen = useMediaQuery('(max-width:1281px)');


  // Check for daily cache refresh on mount
  useEffect(() => {
    if (firebaseCache.shouldRefreshDaily()) {
      console.log("🌅 Daily cache refresh triggered");
      firebaseCache.forceRefreshAll();
    }
  }, []);

  useEffect(() => {
    // Wait for useAdminStaffData to resolve admin status and staff UIDs
    if (staffLoading) return;

    if (isAdmin && userIds.length > 0) {
      // Admin user - fetch clients from all linked staff (including admin self)
      console.log('Admin user - fetching aggregated clients:', userIds);
      dispatch(fetchClientsForMultipleUsers(userIds));
    } else if (user?.uid && !isAdmin) {
      // Regular staff user - fetch their own clients
      console.log("🔄 ClientList: Starting to fetch clients for user:", user.uid);
      dispatch(fetchClients({ agentId: user.uid }));
    }
  }, [userIds, isAdmin, user?.uid, staffLoading, dispatch]);



  // Handle incoming navigation state to set current page and tab
  useEffect(() => {
    if (location.state?.currentPage) {
      setCurrentPage(location.state.currentPage);
    }
    if (location.state?.selectedTab) {
      setSelectedTab(location.state.selectedTab);
    }

    // If returning from a screen that may have modified data, force refetch
    // This ensures deleted clients are removed from the list immediately
    if (location.state?.forceRefetch && user?.uid) {
      console.log("🔄 Force refetching clients due to data modification");
      if (isAdmin && userIds.length > 0) {
        dispatch(fetchClientsForMultipleUsers(userIds));
      } else if (!isAdmin) {
        dispatch(fetchClients({ agentId: user.uid }));
      }
    }
  }, [location.state, user?.uid, dispatch, isAdmin, userIds]);

  // ---- Filtering ----
  const filtered = clients.filter((c) => {
    // Filter based on selected tab
    const isNewLead = c.status === "New Lead";
    const tabMatch =
      selectedTab === "customers"
        ? !isNewLead
        : selectedTab === "new-leads"
          ? isNewLead
          : true;

    if (!tabMatch) return false;

    const searchLower = searchTerm.toLowerCase();
    return (
      (c.name || "").toLowerCase().includes(searchLower) ||
      (c.mobile || "").includes(searchTerm) ||
      (c.referredBy || "").toLowerCase().includes(searchLower)
    );
  });

  // Sort by createdAt (newest first)
  const sorted = filtered.sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
    const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
    return dateB - dateA; // Descending order (newest first)
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));

  // Adjust current page if it's beyond total pages (e.g., after deletion)
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const paginated = sorted.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Calculate counts for each tab
  const tabCounts = getClientCountsByStatus(clients);

  const rows = paginated.map((c, index) => {
    return {
      id: c.id || index,
      name: c.name,
      mobile: c.mobile,
      email: c.email,
      address: c.address,
      original: c
    };
  });

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      {/* Header with title and New Lead button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Roboto Slab',
            fontWeight: 550,
            fontSize: isSmallScreen ? '24px' : '32px',
            fontStyle: 'Medium',
            lineHeight: isSmallScreen ? '40px' : '56px',
            letterSpacing: '0.02em',
            opacity: 1,
            height: isSmallScreen ? 40 : 56,
            textEmphasisColor: '#212121'
          }}
        >
          Client Details
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Tooltip title={!isAccountActive ? 'Account deactivated. Please contact support to renew your subscription.' : ''} arrow>
            <span>
              <Button
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={() => setShowNewLeadModal(true)}
                disabled={!isAccountActive}
                sx={{
                  width: isSmallScreen ? 120 : 140,
                  height: isSmallScreen ? 32 : 48,
                  borderRadius: '8px',
                  p: isSmallScreen ? '6px 8px' : '12px 12px 12px 8px',
                  gap: '4px',
                  boxShadow: 0,
                  backgroundColor: '#0063F7 !important',
                  color: '#FFFFFF !important',
                  fontFamily: 'Roboto',
                  fontWeight: 400,
                  fontSize: isSmallScreen ? '13px' : '14px',
                  lineHeight: isSmallScreen ? '20px' : '24px',
                  letterSpacing: '0.01em',
                  '&:hover': {
                    backgroundColor: '#0063F7 !important',
                    color: '#FFFFFF !important',
                    boxShadow: 10
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#cccccc !important',
                    color: '#888888 !important',
                  }
                }}
              >
                New Lead
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <Box
        sx={{
          width: '100%',
          background: '#fff',
          borderRadius: '0',
          boxShadow: 'none',
          px: isMobile ? 0.5 : { xs: 2, sm: 3 },
          pb: isMobile ? 1 : 3,
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          gap: 0,
          minHeight: 'calc(100vh - 120px)', // Ensure container takes full height
          height: 'calc(100vh - 120px)', // Fixed height to prevent shrinking
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newVal) => {
              setSelectedTab(newVal);
              setCurrentPage(1);
            }}
            variant="standard"
            size="small"
            indicatorColor="primary"
            sx={{
              mb: 1,
              width: 'fit-content',
              justifyContent: 'flex-start',
              '& .MuiTab-root': {
                textTransform: 'capitalize',
                fontWeight: 500,
                minWidth: isSmallScreen ? 60 : 80,
                px: isSmallScreen ? 1.5 : 2,
                py: 0.5,
                fontSize: isSmallScreen ? '13px' : 'inherit',
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: '#E0EBFF',
                },
              }
            }}
          >
            <Tab
              label={`Customers (${tabCounts.customers || 0})`}
              value="customers"
              sx={{
                '&:hover': {
                  backgroundColor: '#E0EBFF',
                },
              }}
            />
            <Tab
              label={`New Leads (${tabCounts['new-leads'] || 0})`}
              value="new-leads"
              sx={{
                '&:hover': {
                  backgroundColor: '#E0EBFF',
                },
              }}
            />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setShowImportDialog(true)}
              sx={{
                borderRadius: '8px',
                px: isSmallScreen ? 1.5 : 2.5,
                height: isSmallScreen ? 32 : 40,
                textTransform: 'none',
                fontWeight: 500,
                fontSize: isSmallScreen ? '13px' : 'inherit',
                color: '#1F2937',
                borderColor: '#E0E0E0',
                '&:hover': {
                  color: '#004DC2',
                  backgroundColor: '#F5F8FF',
                  borderColor: '#E0E0E0'
                }
              }}
            >
              Import
            </Button>
          </Box>
        </Box>
        <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 2, mt: -1 }} />


        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search client by name or phone number"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
          size="small"
          InputProps={{
            startAdornment: (
              <Box sx={{ px: 1, display: 'flex', alignItems: 'center' }}>
                <FiSearch />
              </Box>
            )
          }}
          sx={{ mb: 0.5 }}
        />

        {loading ? (
          <Typography>Loading clients...</Typography>
        ) : (
          <>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: 'none',
                border: 'none',
                backgroundColor: 'transparent',
                borderRadius: 0,
                px: 0,
                mx: isMobile ? -0.5 : { xs: -2, sm: -3 }, // Extend table beyond container padding
                width: isMobile ? 'calc(100% + 8px)' : { xs: 'calc(100% + 32px)', sm: 'calc(100% + 48px)' }, // Compensate for negative margin
                maxHeight: 'none',
                overflow: 'visible', // Allow natural content flow
                // Remove flex properties to prevent row stretching
                '& .MuiTableHead-root .MuiTableCell-root': {
                  borderBottom: '1px solid #E0E0E0',
                  backgroundColor: 'transparent',
                },
                '& .MuiTableBody-root .MuiTableCell-root': {
                  borderBottom: '1px solid #E0E0E0',
                  backgroundColor: 'transparent',
                },
                '& .MuiTableRow-root': {
                  borderRadius: '0 !important',
                },
                '& .MuiTableCell-root': {
                  borderRadius: '0 !important',
                },
                '& .MuiPaper-root': {
                  borderRadius: '0 !important',
                  borderTopLeftRadius: '0 !important',
                  borderTopRightRadius: '0 !important',
                  borderBottomLeftRadius: '0 !important',
                  borderBottomRightRadius: '0 !important',
                  boxShadow: 'none',
                  border: 'none',
                },
                '& .MuiTableBody-root .MuiTableRow-root:last-child': {
                  boxShadow: 'none',
                },
              }}
            >
              <Table
                size="medium"
                aria-label="client table"
                sx={{
                  border: 'none',
                  borderCollapse: 'collapse',
                  tableLayout: isMobile ? 'auto' : 'fixed',
                  width: '100%',
                  // Remove flexGrow to prevent row stretching
                  '& th, & td': {
                    border: 'none',
                    px: 0,
                  },
                  '& td:first-of-type, & th:first-of-type': {
                    pl: 4,
                  },
                  '& td:last-of-type, & th:last-of-type': {
                    pr: 4,
                  },
                  '& tr': {
                    borderBottom: '1px solid #E0E0E0',
                  },
                  '& tr:last-child': {
                    borderBottom: 'none !important',
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ borderBottom: '1px solid #E0E0E0' }}>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #E0E0E0',
                        backgroundColor: 'transparent',
                        px: 0,
                        width: isMobile ? 60 : '10%'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 60, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <span style={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>#</span>
                      </Box>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderBottom: '1px solid #E0E0E0',
                        backgroundColor: 'transparent',
                        px: 0,
                        width: isMobile ? 120 : '25%'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <PersonIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Customer
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: isMobile ? 120 : '20%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <DescriptionIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Email
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #E0E0E0', backgroundColor: 'transparent', px: 0, width: isMobile ? 120 : '35%' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', width: 140, height: 24, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'left' }}>
                        <BusinessIcon sx={{ width: 15, height: '13.333px', color: '#0000008A' }} />
                        <Typography sx={{
                          fontFamily: 'Roboto',
                          fontWeight: 500,
                          fontSize: isSmallScreen ? '12px' : '16px',
                          lineHeight: isSmallScreen ? '18px' : '24px',
                          letterSpacing: '0.01em',
                          color: '#4B5563DE',
                          opacity: 1,
                          whiteSpace: 'nowrap'
                        }}>
                          Address
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow sx={{ height: 48, verticalAlign: 'middle' }}>
                      <TableCell colSpan={4} align="center" sx={{ px: 0 }}>
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                          <Typography sx={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '0.02em',
                            color: '#6B7280',
                            mb: 1
                          }}>
                            No clients found. Great time to follow up with new leads!
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row, index) => (
                      <TableRow
                        key={row.id}
                        hover
                        onClick={() => navigate(`/client/${row.id}`, { state: { client: row.original, fromPage: currentPage, fromTab: selectedTab } })}
                        sx={{
                          height: 48,
                          verticalAlign: 'middle',
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: '#E0EBFF !important' }
                        }}
                      >
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 60 : '10%',
                            maxWidth: isMobile ? 60 : '10%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <div style={{
                            fontFamily: 'Roboto',
                            fontWeight: 400,
                            fontSize: '16px',
                            lineHeight: '24px',
                            letterSpacing: '0.02em',
                            color: '#212121',
                            height: '48px',
                            width: '100%',
                            maxWidth: '100%',
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                            {(currentPage - 1) * itemsPerPage + index + 1}
                          </div>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 120 : '25%',
                            maxWidth: isMobile ? 120 : '25%',
                            verticalAlign: 'middle',
                            overflow: 'hidden',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#212121',
                              mb: 0.5,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                              maxWidth: '100%',
                              display: 'block',
                            }}
                          >
                            {row.name || "-"}
                          </Typography>
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '24px',
                              letterSpacing: '0.01em',
                              color: '#212121',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              width: '100%',
                            }}
                          >
                            {row.mobile || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 120 : '20%',
                            maxWidth: isMobile ? 'none' : '20%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#212121',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              opacity: 1,
                              width: '100%',
                            }}
                          >
                            {row.email || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          sx={{
                            px: 0,
                            width: isMobile ? 120 : '35%',
                            maxWidth: isMobile ? 'none' : '35%',
                            verticalAlign: 'middle',
                          }}
                        >
                          <Typography
                            sx={{
                              fontFamily: 'Roboto',
                              fontWeight: 400,
                              fontSize: '16px',
                              lineHeight: '24px',
                              letterSpacing: '0.02em',
                              color: '#212121',
                              opacity: 1,
                              width: '100%',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {row.address || "-"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}


                </TableBody>
              </Table>
            </TableContainer>

            {/* Spacer to push pagination to bottom */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Pagination moved below */}
            {totalPages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  mt: 'auto', // Push pagination to bottom
                  backgroundColor: '#fff',
                  borderRadius: '0',
                  boxShadow: 'none',
                  pt: 1,
                  pb: 1
                }}
              >
                <Pagination
                  count={Math.max(totalPages, 1)}
                  page={currentPage}
                  onChange={(_, value) => setCurrentPage(value)}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      borderRadius: '50%',
                      border: '1px solid #D1D5DB',
                      width: 36,
                      height: 36,
                      margin: '0 4px',
                      color: '#6B7280',
                      '&:hover': {
                        backgroundColor: '#E0EBFF',
                      },
                    },
                    '& .MuiPaginationItem-previousNext': {
                      borderRadius: 0,
                      border: 'none',
                      backgroundColor: 'transparent',
                      width: 'auto',
                      height: 'auto',
                      margin: '0 4px',
                      color: '#6B7280',
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    },
                    '& .Mui-selected': {
                      backgroundColor: '#004DC2 !important',
                      color: '#fff !important',
                      border: 'none',
                      '&:hover': {
                        backgroundColor: '#004DC2 !important',
                      },
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </Box>

      {showNewLeadModal && (
        <UnifiedPolicyForm
          isOpen={showNewLeadModal}
          onClose={() => {
            setShowNewLeadModal(false);
          }}
          prefillData={{
            status: "New Lead"
          }}
          onSuccess={(pdfUrl, formData) => {
            setShowNewLeadModal(false);

            // Show success toast
            toast.success("New lead added successfully!", {
              containerId: "bottom-center",
              position: "bottom-center",
              autoClose: 2000,
              hideProgressBar: true,
              closeOnClick: false,
              pauseOnHover: false,
              draggable: false,
              closeButton: false,
              style: {
                backgroundColor: '#DCFCE7',
                color: '#FFFFFF',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 500,
                padding: '12px 16px',
                minHeight: '44px',
                alignItems: 'center',
                gap: '2px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                marginBottom: '75vh',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                width: 'auto',
                maxWidth: 'none',
                display: 'inline-flex',
              },
              icon: (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{ color: '#FFFFFF' }}
                >
                  <path
                    d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )
            });

            // Switch to new leads tab if not already there
            if (selectedTab !== 'new-leads') {
              setSelectedTab('new-leads');
            }

            // Mark cache for invalidation (ClientList write operation)
            // This invalidates ClientList, PolicyList, and Renewals
            firebaseCache.invalidateAfterWrite('clientlist_write', user?.uid);

            // Update aggregation for the new lead only (not all clients)
            if (updateClientAggregation && formData) {
              updateClientAggregation({
                _operation: 'add',
                status: 'New Lead',
                name: formData.name,
                mobile: formData.mobile
              });
            }

            // Refresh the client list to show the new lead immediately
            if (user?.uid) {
              dispatch(fetchClients({ agentId: user.uid }));
            }
          }}
          requiredFields={['name', 'mobile', 'expiryDate']}
          formTitle="New Lead"
        />
      )}

      {/* Import Policies Dialog */}
      <ImportPoliciesDialog
        open={showImportDialog}
        onClose={() => setShowImportDialog(false)}
        onSuccess={() => {
          // Clear Redux cache and invalidation
          dispatch(clearClientCache({ agentId: user?.uid }));
          firebaseCache.invalidateAfterWrite('clientlist_write', user?.uid);

          if (isAdmin && userIds.length > 0) {
            dispatch(fetchClientsForMultipleUsers(userIds));
          } else if (user?.uid) {
            dispatch(fetchClients({ agentId: user.uid }));
          }
        }}
      />
    </Box>
  );
};

export default ClientList;
